import { DestroyRef, Directive, effect, ElementRef, inject, input, NgZone } from "@angular/core";

const DURATION_MS = 2000;
const SMART_EASING_THRESHOLD = 999;
const SMART_EASING_AMOUNT = 333;
const GROUP_SEPARATOR = ",";
const DECIMAL_SEPARATOR = ".";

const easeOutExpo = (time: number, begin: number, change: number, duration: number): number =>
  (change * (-Math.pow(2, (-10 * time) / duration) + 1) * 1024) / 1023 + begin;

/**
 * Animates the host element's text from its current value up (or down) to the given value.
 *
 * Large jumps are split in two legs: the first runs without easing up to `SMART_EASING_AMOUNT`
 * away from the target, the second eases over the remainder, so long counts stay readable.
 */
@Directive({
  selector: "[adaaCountUp]",
})
export class CountUpDirective {
  private readonly _element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _zone = inject(NgZone);

  readonly value = input.required<number>({ alias: "adaaCountUp" });
  readonly suffix = input<string>("");
  readonly decimalPlaces = input<number>(0);

  private _frameId: number | null = null;
  private _frameValue = 0;
  private _suffix = "";
  private _decimalPlaces = 0;

  constructor() {
    inject(DestroyRef).onDestroy(() => this._cancel());

    effect(() => {
      this._suffix = this.suffix();
      this._decimalPlaces = Math.max(0, this.decimalPlaces());

      this._animateTo(this.value());
    });
  }

  private _animateTo(value: number): void {
    this._cancel();

    if (!Number.isFinite(value) || value === this._frameValue) {
      this._frameValue = Number.isFinite(value) ? value : this._frameValue;
      this._render(this._frameValue);
      return;
    }

    const startValue = this._frameValue;
    if (Math.abs(value - startValue) <= SMART_EASING_THRESHOLD) {
      this._runLeg(startValue, value, DURATION_MS, true);
      return;
    }

    const countingDown = startValue > value;
    const breakpoint = value + (countingDown ? SMART_EASING_AMOUNT : -SMART_EASING_AMOUNT);
    const legDuration = DURATION_MS / 2;

    this._runLeg(startValue, breakpoint, legDuration, false, () => this._runLeg(breakpoint, value, legDuration, true));
  }

  private _runLeg(from: number, to: number, durationMs: number, useEasing: boolean, onComplete?: () => void): void {
    const countingDown = from > to;
    let startTime: number | null = null;

    const step = (timestamp: number): void => {
      startTime ??= timestamp;
      const progress = timestamp - startTime;

      let frame: number;
      if (useEasing) {
        frame = countingDown
          ? from - easeOutExpo(progress, 0, from - to, durationMs)
          : easeOutExpo(progress, from, to - from, durationMs);
      } else {
        frame = from + (to - from) * (progress / durationMs);
      }

      const wentPast = countingDown ? frame < to : frame > to;
      this._frameValue = Number((wentPast ? to : frame).toFixed(this._decimalPlaces));
      this._render(this._frameValue);

      if (progress < durationMs) {
        this._frameId = requestAnimationFrame(step);
        return;
      }

      this._frameId = null;
      onComplete?.();
    };

    this._zone.runOutsideAngular(() => {
      this._frameId = requestAnimationFrame(step);
    });
  }

  private _render(value: number): void {
    this._element.nativeElement.textContent = this._format(value);
  }

  private _format(value: number): string {
    const sign = value < 0 ? "-" : "";
    const [whole, fraction] = Math.abs(value).toFixed(this._decimalPlaces).split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);

    return `${sign}${grouped}${fraction ? DECIMAL_SEPARATOR + fraction : ""}${this._suffix}`;
  }

  private _cancel(): void {
    if (this._frameId === null) return;

    cancelAnimationFrame(this._frameId);
    this._frameId = null;
  }
}
